import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import notificationsService from '../services/notificationsService';

const TYPE_STYLE = {
  booking: { icon: 'lucide:calendar-check', bg: 'bg-primary/10', color: 'text-primary' },
  payment: { icon: 'lucide:credit-card', bg: 'bg-green-500/10', color: 'text-green-500' },
  system: { icon: 'lucide:bell', bg: 'bg-orange-500/10', color: 'text-orange-500' },
  user: { icon: 'lucide:user-plus', bg: 'bg-purple-500/10', color: 'text-purple-500' },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (days > 0) return `${days} day${days === 1 ? '' : 's'} ago`;
  if (hours > 0) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  if (mins > 0) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  return 'Just now';
}

function getResourceLink(notification) {
  const meta = notification.metadata || {};
  if (notification.type === 'booking' && meta.bookingId) return `/bookings/${meta.bookingId}`;
  if (notification.type === 'user' && meta.userId) return `/users/profile/${meta.userId}`;
  return null;
}

const NotificationItem = ({ notification, onMarkRead, onDelete }) => {
  const style = TYPE_STYLE[notification.type] || TYPE_STYLE.system;
  const link = getResourceLink(notification);

  return (
    <div className={`bg-card p-4 rounded-2xl border border-border shadow-sm hover:shadow-md transition-all group flex gap-4 items-start relative overflow-hidden ${!notification.isRead ? '' : 'opacity-80 hover:opacity-100'}`}>
      {!notification.isRead && <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-2xl" />}
      <div className={`w-12 h-12 ${style.bg} rounded-xl flex items-center justify-center shrink-0`}>
        <Icon icon={style.icon} className={`text-2xl ${style.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h3 className={`text-sm font-bold text-foreground truncate ${!notification.isRead ? 'group-hover:text-primary' : ''}`}>
            {notification.title}
          </h3>
          <span className="text-[10px] font-bold text-muted-foreground whitespace-nowrap uppercase tracking-tighter">
            {timeAgo(notification.createdAt)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">{notification.message}</p>
        <div className="flex items-center gap-4 mt-3">
          {link && (
            <Link to={link} className="text-xs font-bold text-primary hover:underline underline-offset-4">
              View Details
            </Link>
          )}
          {!notification.isRead && (
            <button onClick={() => onMarkRead(notification.id)} className="text-xs font-bold text-muted-foreground hover:text-foreground">
              Mark as read
            </button>
          )}
          <button onClick={() => onDelete(notification.id)} className="text-xs font-bold text-destructive/60 hover:text-destructive">
            Delete
          </button>
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        {!notification.isRead && <div className="w-2.5 h-2.5 bg-primary rounded-full" />}
      </div>
    </div>
  );
};

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'booking', label: 'Bookings' },
  { key: 'payment', label: 'Payments' },
  { key: 'system', label: 'System' },
  { key: 'user', label: 'Users' },
];

export default function NotificationCenter() {
  const [activeTab, setActiveTab] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const LIMIT = 20;

  const fetchNotifications = useCallback(async (tab = activeTab, pg = page) => {
    setLoading(true);
    try {
      const data = await notificationsService.getNotifications({
        page: pg,
        limit: LIMIT,
        type: tab === 'all' ? undefined : tab,
      });
      if (pg === 1) {
        setNotifications(data.notifications || []);
      } else {
        setNotifications(prev => [...prev, ...(data.notifications || [])]);
      }
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page]);

  useEffect(() => {
    setPage(1);
    fetchNotifications(activeTab, 1);
  }, [activeTab]);

  const handleMarkRead = async (id) => {
    await notificationsService.markAsRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
  };

  const handleMarkAllRead = async () => {
    await notificationsService.markAllAsRead();
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const handleDelete = async (id) => {
    await notificationsService.deleteNotification(id);
    setNotifications(prev => prev.filter(n => n.id !== id));
    setTotal(prev => Math.max(0, prev - 1));
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNotifications(activeTab, nextPage);
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const filtered = searchTerm
    ? notifications.filter(n =>
        n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        n.message.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : notifications;

  const hasMore = notifications.length < total;

  return (
    <PageLayout>
      <PageHeader
        title="Notifications Center"
        description="Manage system alerts and user activity."
        customContent={
          <>
            <div className="relative group">
              <Icon icon="lucide:search" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <input
                type="text"
                placeholder="Filter alerts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-muted border border-transparent rounded-xl text-sm focus:bg-card focus:border-primary focus:ring-4 focus:ring-primary/10 w-48 md:w-64 transition-all outline-none"
              />
            </div>
          </>
        }
      />

      <div className="flex-1 overflow-y-auto p-8 space-y-6 scroll-smooth">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center bg-muted p-1 rounded-xl w-fit gap-1">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === tab.key ? 'bg-card shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {tab.key === 'all' && unreadCount > 0 ? `${tab.label} (${unreadCount})` : tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleMarkAllRead} className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-muted-foreground hover:text-foreground transition-all">
              <Icon icon="lucide:check-check" className="text-lg" />
              Mark all as read
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {loading && notifications.length === 0 ? (
            <div className="text-center py-12">
              <Icon icon="lucide:loader-2" className="text-4xl text-muted-foreground mx-auto mb-4 animate-spin" />
              <p className="text-muted-foreground">Loading notifications…</p>
            </div>
          ) : filtered.length > 0 ? (
            filtered.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onMarkRead={handleMarkRead}
                onDelete={handleDelete}
              />
            ))
          ) : (
            <div className="text-center py-12">
              <Icon icon="lucide:inbox" className="text-4xl text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No notifications found</p>
              <p className="text-sm text-muted-foreground mt-2">
                {searchTerm ? 'Try adjusting your search' : 'Notifications will appear here as activity occurs'}
              </p>
            </div>
          )}
        </div>

        {hasMore && !loading && (
          <div className="flex items-center justify-center py-6">
            <button
              onClick={handleLoadMore}
              className="px-8 py-3 bg-card border border-border rounded-xl text-sm font-bold hover:bg-muted transition-all active:scale-95 shadow-sm flex items-center gap-2"
            >
              <Icon icon="lucide:refresh-cw" className="text-lg" />
              Load Older Notifications
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
